// Mirror of the evaluator in backend/internal/handlers/formula.go.
//
// Two implementations is a cost, but the alternative is a round trip on every
// keystroke while a reviewer scores. The backend remains the authority: what it
// computes is what gets stored and what every admin view aggregates. This copy
// exists so the reviewer sees the number update as they work, and so an admin
// can test a formula before saving it.
//
// Supported: + - * / parentheses, numbers, {Criterion name}, and the built-ins
// SUM, MAX and COUNT. Nothing else.

function tokenize(src) {
  var out = [];
  var i = 0;
  while (i < src.length) {
    var c = src[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    if (c === '{') {
      var close = src.indexOf('}', i);
      if (close < 0) throw new Error('a { is never closed');
      out.push({kind: 'var', name: src.slice(i + 1, close).trim()});
      i = close + 1;
      continue;
    }
    if (c === '(' || c === ')') { out.push({kind: c}); i++; continue; }
    if (c === '+' || c === '-' || c === '*' || c === '/') { out.push({kind: 'op', op: c}); i++; continue; }
    if ((c >= '0' && c <= '9') || c === '.') {
      var j = i;
      while (j < src.length && ((src[j] >= '0' && src[j] <= '9') || src[j] === '.')) j++;
      var n = parseFloat(src.slice(i, j));
      if (isNaN(n)) throw new Error('could not read the number ' + src.slice(i, j));
      out.push({kind: 'num', num: n});
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      var k = i;
      while (k < src.length && /[A-Za-z0-9_]/.test(src[k])) k++;
      out.push({kind: 'var', name: src.slice(i, k)});
      i = k;
      continue;
    }
    throw new Error('unexpected character ' + c);
  }
  return out;
}

function precedence(op) { return (op === '*' || op === '/') ? 2 : 1; }

export function evaluateFormula(expr, vars) {
  var tokens = tokenize(expr || '');
  if (tokens.length === 0) throw new Error('the formula is empty');

  var values = [];
  var ops = [];

  function apply() {
    var op = ops.pop();
    if (!op || op.kind !== 'op') throw new Error('mismatched brackets');
    if (values.length < 2) throw new Error('an operator is missing a value');
    var b = values.pop();
    var a = values.pop();
    if (op.op === '+') values.push(a + b);
    else if (op.op === '-') values.push(a - b);
    else if (op.op === '*') values.push(a * b);
    else {
      if (b === 0) throw new Error('division by zero');
      values.push(a / b);
    }
  }

  var prev = '';
  for (var t = 0; t < tokens.length; t++) {
    var tok = tokens[t];
    if (tok.kind === 'num') {
      values.push(tok.num);
    } else if (tok.kind === 'var') {
      var key = String(tok.name).trim().toLowerCase();
      if (!(key in vars)) throw new Error('unknown name "' + tok.name + '"');
      values.push(vars[key]);
    } else if (tok.kind === '(') {
      ops.push(tok);
    } else if (tok.kind === ')') {
      while (ops.length && ops[ops.length - 1].kind !== '(') apply();
      if (!ops.length) throw new Error('a ) has no matching (');
      ops.pop();
    } else {
      // Unary minus
      if (tok.op === '-' && (prev === '' || prev === 'op' || prev === '(')) values.push(0);
      while (ops.length && ops[ops.length - 1].kind === 'op' &&
             precedence(ops[ops.length - 1].op) >= precedence(tok.op)) apply();
      ops.push(tok);
    }
    prev = tok.kind;
  }

  while (ops.length) {
    if (ops[ops.length - 1].kind === '(') throw new Error('a ( is never closed');
    apply();
  }
  if (values.length !== 1) throw new Error('malformed formula');
  return values[0];
}

function isNumericType(t) {
  return ['slider', 'radio', 'number', 'select', '', undefined, null].indexOf(t) >= 0;
}

// getScore reads a criterion's score out of the reviewer's working state.
// An unscored criterion counts as 0 so a partially filled review still shows a
// running number rather than an error.
export function buildFormulaVars(criteria, getScore) {
  var vars = {};
  var sum = 0, max = 0, count = 0;

  (criteria || []).forEach(function(c) {
    if (!isNumericType(c.scoring_type)) return;
    var weight = c.weight || 1;
    var raw = getScore(c.id);
    var value = (typeof raw === 'number') ? raw : 0;
    if (typeof raw === 'number') count++;
    vars[String(c.name || '').trim().toLowerCase()] = value;
    sum += value * weight;
    max += (c.max_score || 0) * weight;
  });

  vars.sum = sum;
  vars.max = max;
  vars.count = count;
  return vars;
}

// Convenience for the reviewer screen: returns the formula result when one is
// configured and valid, otherwise the weighted sum.
export function finalScoreFor(criteria, getScore, formula, enabled) {
  var vars = buildFormulaVars(criteria, getScore);
  if (enabled && formula && formula.trim()) {
    try {
      return { value: evaluateFormula(formula, vars), error: '' };
    } catch (e) {
      return { value: vars.sum, error: e.message };
    }
  }
  return { value: vars.sum, error: '' };
}
